import React from 'react';
import { CheckCircle2, Globe2, LayoutDashboard, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import artistIntroLogo from '../../assets/artist_intro.svg';
import { usePlan } from '@/context/PlanContext';
import WebsitePreviewPanel from './WebsitePreviewPanel';

interface VerifiedScreenProps {
  onGoToDashboard: () => void;
  onLogout?: () => void;
}

const VerifiedScreen: React.FC<VerifiedScreenProps> = ({ onGoToDashboard, onLogout }) => {
  const { openUpgradeModal, planInfo } = usePlan();
  const isPublished = planInfo?.isPublished === true;

  return (
    <div className="h-full flex flex-col bg-white rounded-2xl overflow-hidden">
      {/* <div className="flex flex-col lg:grid lg:grid-cols-5 flex-1 min-h-0 overflow-hidden"></div> */}
      <div className="flex flex-col lg:grid lg:grid-cols-5 flex-1 min-h-0 overflow-hidden">
        {/* ─── Left Column: Content ───────────────── */}
        <div className="lg:col-span-2 flex flex-col flex-1 min-h-0 overflow-y-auto">
          <div className="px-6 sm:px-10 lg:px-16 pt-8 lg:pt-10 flex-1">
            <div className="max-w-2xl mx-auto w-full">
              {/* Logo */}
              <img src={artistIntroLogo} alt="Artist Intro" className="h-10 mb-12 shrink-0" />

              {/* Heading */}
              <p className="text-primary font-bold text-2xl sm:text-3xl mb-1">
                {isPublished ? 'Congratulations!' : 'Approved'}
              </p>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 leading-tight">
                {isPublished ? 'Your website is now live.' : 'Your website is ready to publish.'}
              </h1>

              {isPublished ? (
                <div className="mb-8 rounded-lg border border-green-200 bg-green-50 p-4 text-green-800">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
                    <p className="text-sm leading-relaxed">Your plan is active and publication has been confirmed. You can manage your content and website settings from the dashboard.</p>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-gray-500 text-sm sm:text-base mb-1 leading-relaxed">
                    Review your private preview, then choose the plan that fits your release.
                  </p>
                  <p className="text-gray-500 text-sm sm:text-base mb-8 leading-relaxed">
                    Your website remains private until payment or a promo code is confirmed by the server.
                  </p>
                </>
              )}

              {/* Go to Dashboard button */}
             
<div className="mb-8 flex flex-col sm:flex-row items-start sm:items-center gap-3">
  {isPublished ? (
    <Button variant="primary" type="button" onClick={onGoToDashboard} className="w-full sm:w-auto">
      <LayoutDashboard className="w-4 h-4 mr-2" />
      Go to Dashboard
    </Button>
  ) : (
    <Button variant="primary" type="button" onClick={() => openUpgradeModal('publish')} className="w-full sm:w-auto">
      <Globe2 className="w-4 h-4 mr-2" />
      Publish Website
    </Button>
  )}
</div>
            </div>
          </div>

          {/* Sign Out — pinned to bottom */}
          {onLogout && (
            <div className="shrink-0 px-6 sm:px-10 lg:px-16 pb-8">
              <div className="max-w-2xl mx-auto w-full">
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={onLogout}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-3 min-h-100 border-t border-border lg:border-l lg:border-t-0">
          <WebsitePreviewPanel
            statusText={isPublished ? 'Live website' : 'Approved website preview'}
            titleText={isPublished ? 'Live website' : undefined}
          />
        </div>
      </div>
    </div>
  );
};

export default VerifiedScreen;
